import React, { useState } from 'react';
import { useNavigate } from '../../navigation';
import { useSelector } from 'react-redux';
import { Drawer } from 'antd';
import { Squares2X2Icon } from '@heroicons/react/24/outline';
import { RootState } from '../../rootReducer';
import { useAppDispatch } from '../../store';
import { ResolvedQRLink } from '../../interfaces/qr_links';
import {
  LinkedNavNode,
  popToNode,
  pushNode,
  setLinksForQr,
} from '../../features/linkedNav/linkedNav';
import { qrLinksApi } from '../../api/qrLinks';
import { getRestaurant } from '../../features/restaurant/restaurant';
import { applyLinkedCatalogParams } from './applyLinkedCatalogParams';
import LinkedCatalogCard from './components/LinkedCatalogCard';
import ExploreBreadcrumb from './components/ExploreBreadcrumb';

// Mounted once in `Menu/index.tsx` (MenuRouter) so it lives across every
// `/feed/restaurants/:r_id/*` sub-route. Renders a FAB that opens a Drawer listing the
// current qr's linked catalogs (rank order) plus a breadcrumb of the guest's explore path.
const Explore: React.FC = () => {
  const [open, setOpen] = useState(false);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const currentQr = useSelector<RootState, string | null>(
    state => state.cart.qr_code,
  );
  const linksByQr = useSelector<RootState, { [key: string]: ResolvedQRLink[] }>(
    state => state.linkedNav.linksByQr,
  );
  const navStack = useSelector<RootState, LinkedNavNode[]>(
    state => state.linkedNav.navStack,
  );

  const links = (currentQr !== null ? linksByQr[currentQr] : null) ?? [];
  const sortedLinks = [...links].sort((a, b) => a.rank - b.rank);

  const visible = sortedLinks.length !== 0 || navStack.length > 1;

  // Resolve the current node's own links so trees deeper than one level work. The App
  // bootstrap effect ([] deps) only fetches the root QR's links on a full page load; every
  // subsequent Explore tap navigates client-side (no remount), so each newly-entered node
  // must fetch its links here. Guarded on `undefined` so we don't refetch what we already have.
  React.useEffect(() => {
    if (currentQr !== null && linksByQr[currentQr] === undefined) {
      qrLinksApi
        .getLinks(currentQr)
        .then(response => {
          dispatch(
            setLinksForQr({ qr: currentQr, links: response.data.links }),
          );
        })
        .catch(() => {});
    }
  }, [currentQr]);

  if (!visible) {
    return <></>;
  }

  const appendLinked = (relativeUrl: string) => {
    if (relativeUrl.includes('linked=1')) {
      return relativeUrl;
    }
    return `${relativeUrl}${relativeUrl.includes('?') ? '&' : '?'}linked=1`;
  };

  const handleLinkTap = (link: ResolvedQRLink) => {
    // Fire-and-forget analytics -- never block navigation on it.
    qrLinksApi.logClick(link.id);
    dispatch(getRestaurant(link.target_restaurant_id));

    const parsed = new URL(link.guest_url);
    const targetQr = parsed.searchParams.get('qr') ?? `link-${link.id}`;
    const node: LinkedNavNode = {
      qr: targetQr,
      r_id: link.target_restaurant_id,
      name: link.target_restaurant_name ?? link.display_name,
      url: `${parsed.pathname}${parsed.search}`,
    };

    // Cycle handling: if this link leads back to a qr already on the stack, pop back to
    // it instead of growing the stack forever.
    const alreadyOnStack = navStack.some(n => n.qr === targetQr);
    if (alreadyOnStack) {
      dispatch(popToNode(targetQr));
    } else {
      dispatch(pushNode(node));
    }

    applyLinkedCatalogParams(dispatch, parsed);
    setOpen(false);
    navigate(`${parsed.pathname}${parsed.search}`);
  };

  const handleBreadcrumbTap = (node: LinkedNavNode) => {
    dispatch(getRestaurant(node.r_id));
    dispatch(popToNode(node.qr));

    const navUrl = appendLinked(node.url);
    applyLinkedCatalogParams(dispatch, new URL(navUrl, window.location.origin));
    setOpen(false);
    navigate(navUrl);
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Explore linked catalogs"
        className="fixed bottom-28 right-5 z-50 flex items-center justify-center w-12 h-12 rounded-full bg-primary text-on-primary shadow-lg"
      >
        <Squares2X2Icon className="w-6 h-6" />
      </button>
      <Drawer
        title="Explore"
        placement="bottom"
        height="70%"
        open={open}
        onClose={() => setOpen(false)}
        maskClosable={true}
      >
        <div className="space-y-4">
          {navStack.length > 1 && (
            <ExploreBreadcrumb
              navStack={navStack}
              onSelect={handleBreadcrumbTap}
            />
          )}
          {sortedLinks.length !== 0 ? (
            <div className="space-y-2">
              {sortedLinks.map(link => (
                <LinkedCatalogCard
                  key={link.id}
                  link={link}
                  onClick={() => handleLinkTap(link)}
                />
              ))}
            </div>
          ) : (
            <div className="text-sm text-on-surface-variant p-4 text-center">
              No linked catalogs here.
            </div>
          )}
        </div>
      </Drawer>
    </>
  );
};

export default Explore;
